import crypto from "node:crypto";
import { createPublicKey, verify as verifyEd25519 } from "node:crypto";

import { safeSameOriginPath } from "./telegramRuntimeEnv";

import type { TelegramLoginData } from "./verifyTelegramLogin";

const MAX_AUTH_AGE_SEC = 24 * 60 * 60;
const MAX_CLOCK_SKEW_SEC = 5 * 60;
const DEFAULT_MINI_APP_REDIRECT = "/bookings/upcoming";
const INIT_DATA_EXCLUDED_KEYS = new Set(["hash", "signature"]);
const TELEGRAM_ED25519_PUBLIC_KEY_HEX_PRODUCTION =
  "e7bf03a2fa4602af4580703d88dda5bb59f32ed8b02a56c187fe7d34caed242d";
const MINI_APP_USERNAME_RE = /^[a-zA-Z0-9_-]+$/;

/** Validate booking username from Mini App `?u=` (same charset as start_param `u_*`). */
export function normalizeTelegramMiniAppUsername(
  username: string | null | undefined
): string | null {
  const value = (username || "").trim();
  if (!value || !MINI_APP_USERNAME_RE.test(value)) return null;
  return value;
}

/**
 * Read Telegram launch query `tgWebAppStartParam` from location search or hash.
 * Telegram duplicates `startapp` here; useful when `initDataUnsafe.start_param` is empty.
 */
export function readTgWebAppStartParamFromLocation(
  search: string = "",
  hash: string = ""
): string | undefined {
  const fromParams = (raw: string): string | undefined => {
    const normalized = raw.startsWith("?") || raw.startsWith("#") ? raw.slice(1) : raw;
    if (!normalized || !normalized.includes("=")) return undefined;
    try {
      const value = new URLSearchParams(normalized).get("tgWebAppStartParam")?.trim();
      return value || undefined;
    } catch {
      return undefined;
    }
  };

  return fromParams(search) || fromParams(hash) || undefined;
}

/**
 * Prefer Telegram `start_param` / `startapp`, then `tgWebAppStartParam`, else synthesize `u_{username}` from `?u=`.
 */
export function resolveTelegramMiniAppEffectiveStartParam(
  startParam: string | null | undefined,
  queryUsername: string | null | undefined,
  tgWebAppStartParam?: string | null | undefined
): string | undefined {
  const fromStart = (startParam || "").trim();
  if (fromStart) return fromStart;

  const fromLaunchQuery = (tgWebAppStartParam || "").trim();
  if (fromLaunchQuery) return fromLaunchQuery;

  const safeUser = normalizeTelegramMiniAppUsername(queryUsername);
  if (!safeUser) return undefined;
  return `u_${safeUser}`;
}

/** True when current path (+ search) already matches a start_param redirect target. */
export function telegramStartParamTargetMatches(
  pathname: string | null | undefined,
  search: string | null | undefined,
  target: string
): boolean {
  const path = (pathname || "").trim() || "/";
  let targetUrl: URL;
  try {
    targetUrl = new URL(target, "http://local.invalid");
  } catch {
    return false;
  }
  if (path !== targetUrl.pathname) return false;

  const current = new URLSearchParams(
    (search || "").startsWith("?") ? (search || "").slice(1) : search || ""
  );
  const required = Array.from(targetUrl.searchParams.entries());
  for (const [key, value] of required) {
    if (current.get(key) !== value) return false;
  }
  return true;
}

type TelegramWebAppUser = {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
};

export type TelegramWebAppInitDataValidation =
  | { ok: true; method: "hmac" | "ed25519" }
  | { ok: false; reason: "malformed" | "hmac" | "expired" };

export function normalizeBotToken(botToken: string): string {
  return botToken.trim().replace(/^Bot\s+/i, "").replace(/^["']|["']$/g, "");
}

function normalizeInitData(initData: string): string {
  return initData.trim().replace(/^[?#]/, "");
}

function decodeInitDataComponent(part: string): string {
  try {
    return decodeURIComponent(part.replace(/\+/g, "%20"));
  } catch {
    return part;
  }
}

/** Parse initData query string without URLSearchParams (+ / encoding pitfalls). */
export function parseInitDataQuery(initData: string): Map<string, string> {
  const pairs = new Map<string, string>();
  const normalized = normalizeInitData(initData);

  for (const segment of normalized.split("&")) {
    if (!segment) continue;
    const eq = segment.indexOf("=");
    if (eq < 0) continue;
    const key = decodeInitDataComponent(segment.slice(0, eq));
    const value = decodeInitDataComponent(segment.slice(eq + 1));
    pairs.set(key, value);
  }

  return pairs;
}

function buildDataCheckString(pairs: Map<string, string>): string {
  return Array.from(pairs.entries())
    .filter(([key]) => !INIT_DATA_EXCLUDED_KEYS.has(key))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}=${value}`)
    .join("\n");
}

/** Fallback: keep percent-encoded values as in the raw query string. */
function buildEncodedDataCheckString(initData: string): string {
  const segments: string[] = [];
  const normalized = normalizeInitData(initData);

  for (const segment of normalized.split("&")) {
    if (!segment) continue;
    const eq = segment.indexOf("=");
    if (eq < 0) continue;
    const rawKey = segment.slice(0, eq);
    if (INIT_DATA_EXCLUDED_KEYS.has(decodeInitDataComponent(rawKey))) continue;
    segments.push(segment);
  }

  return segments.sort((a, b) => a.localeCompare(b)).join("\n");
}

/** Full-string decode then split — matches common Telegram gist implementations. */
function buildGistDataCheckString(initData: string): string {
  try {
    const decoded = decodeURIComponent(normalizeInitData(initData));
    return decoded
      .split("&")
      .filter((segment) => {
        if (!segment) return false;
        return !segment.startsWith("hash=") && !segment.startsWith("signature=");
      })
      .sort((a, b) => a.localeCompare(b))
      .join("\n");
  } catch {
    return "";
  }
}

function buildDataCheckStringIncludingSignature(pairs: Map<string, string>): string {
  return Array.from(pairs.entries())
    .filter(([key]) => key !== "hash")
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}=${value}`)
    .join("\n");
}

function computeInitDataHash(dataCheckString: string, botToken: string): string {
  const secretKey = crypto.createHmac("sha256", "WebAppData").update(botToken).digest();
  return crypto.createHmac("sha256", secretKey).update(dataCheckString).digest("hex");
}

function hashesEqual(expected: string, actual: string): boolean {
  const a = expected.trim().toLowerCase();
  const b = actual.trim().toLowerCase();
  if (a.length !== b.length) return false;
  try {
    return crypto.timingSafeEqual(Buffer.from(a, "utf8"), Buffer.from(b, "utf8"));
  } catch {
    return a === b;
  }
}

function base64UrlToBuffer(input: string): Buffer | null {
  try {
    const normalized = input.replace(/-/g, "+").replace(/_/g, "/");
    const pad = normalized.length % 4 === 0 ? "" : "=".repeat(4 - (normalized.length % 4));
    return Buffer.from(normalized + pad, "base64");
  } catch {
    return null;
  }
}

function getTelegramEd25519PublicKey(): crypto.KeyObject {
  const raw = Buffer.from(TELEGRAM_ED25519_PUBLIC_KEY_HEX_PRODUCTION, "hex");
  const derPrefix = Buffer.from("302a300506032b656e032100", "hex");
  return createPublicKey({
    key: Buffer.concat([derPrefix, raw]),
    format: "der",
    type: "spki",
  });
}

/** Bot API 8.0+ — Ed25519 signature validation (see core.telegram.org/bots/webapps). */
function verifyEd25519InitData(pairs: Map<string, string>, botToken: string): boolean {
  const signature = pairs.get("signature");
  if (!signature) return false;

  const botId = normalizeBotToken(botToken).split(":")[0];
  if (!botId || !/^\d+$/.test(botId)) return false;

  const body = buildDataCheckString(pairs);
  const dataCheckString = `${botId}:WebAppData\n${body}`;
  const sig = base64UrlToBuffer(signature);
  if (!sig) return false;

  try {
    return verifyEd25519(null, Buffer.from(dataCheckString, "utf8"), getTelegramEd25519PublicKey(), sig);
  } catch {
    return false;
  }
}

function isAuthDateValid(authDate: string | undefined): boolean {
  if (!authDate) return false;
  const authTs = Number.parseInt(authDate, 10);
  if (!Number.isFinite(authTs)) return false;
  const ageSec = Math.floor(Date.now() / 1000) - authTs;
  return ageSec >= -MAX_CLOCK_SKEW_SEC && ageSec <= MAX_AUTH_AGE_SEC;
}

function verifyHmacInitData(pairs: Map<string, string>, normalized: string, botToken: string): boolean {
  const hash = pairs.get("hash");
  if (!hash) return false;

  const candidates = [
    buildDataCheckString(pairs),
    buildEncodedDataCheckString(normalized),
    buildGistDataCheckString(normalized),
    buildDataCheckStringIncludingSignature(pairs),
  ].filter(Boolean);

  for (const dataCheckString of candidates) {
    const computed = computeInitDataHash(dataCheckString, botToken);
    if (hashesEqual(hash, computed)) return true;
  }

  return false;
}

export function validateTelegramWebAppInitData(
  initData: string,
  botToken: string
): TelegramWebAppInitDataValidation {
  const token = normalizeBotToken(botToken);
  const normalized = normalizeInitData(initData);
  if (!normalized || !token) {
    return { ok: false, reason: "malformed" };
  }

  const pairs = parseInitDataQuery(normalized);
  if (!pairs.get("auth_date") || !pairs.get("user")) {
    return { ok: false, reason: "malformed" };
  }

  const hmacOk = verifyHmacInitData(pairs, normalized, token);
  const ed25519Ok = !hmacOk ? verifyEd25519InitData(pairs, token) : false;

  if (!hmacOk && !ed25519Ok) {
    return { ok: false, reason: "hmac" };
  }

  if (!isAuthDateValid(pairs.get("auth_date"))) {
    return { ok: false, reason: "expired" };
  }

  return { ok: true, method: hmacOk ? "hmac" : "ed25519" };
}

export function verifyTelegramWebAppInitData(initData: string, botToken: string): boolean {
  return validateTelegramWebAppInitData(initData, botToken).ok;
}

export function parseTelegramWebAppInitData(initData: string): TelegramLoginData | null {
  const pairs = parseInitDataQuery(initData);
  const hash = pairs.get("hash") || pairs.get("signature") || "webapp";
  const auth_date = pairs.get("auth_date");
  const userRaw = pairs.get("user");
  if (!auth_date || !userRaw) return null;

  let user: TelegramWebAppUser;
  try {
    user = JSON.parse(userRaw) as TelegramWebAppUser;
  } catch {
    return null;
  }
  if (!user?.id) return null;

  return {
    id: String(user.id),
    hash,
    auth_date,
    first_name: user.first_name,
    last_name: user.last_name,
    username: user.username,
    photo_url: user.photo_url,
  };
}

/** Map Telegram Mini App start_param to a safe same-origin redirect path. */
export function resolveTelegramStartParamRedirect(startParam: string | null | undefined): string {
  const raw = (startParam || "").trim();
  if (!raw) {
    return DEFAULT_MINI_APP_REDIRECT;
  }

  if (raw.startsWith("u_")) {
    const username = raw.slice(2).trim();
    if (username && /^[a-zA-Z0-9_-]+$/.test(username)) {
      return safeSameOriginPath(`/${username}`) || DEFAULT_MINI_APP_REDIRECT;
    }
    return DEFAULT_MINI_APP_REDIRECT;
  }

  if (raw.startsWith("et_")) {
    const rest = raw.slice(3);
    const sep = rest.indexOf("_");
    if (sep > 0) {
      const username = rest.slice(0, sep);
      const slug = rest.slice(sep + 1);
      if (
        username &&
        slug &&
        /^[a-zA-Z0-9_-]+$/.test(username) &&
        /^[a-zA-Z0-9_-]+$/.test(slug)
      ) {
        return safeSameOriginPath(`/${username}/${slug}`) || DEFAULT_MINI_APP_REDIRECT;
      }
    }
    return DEFAULT_MINI_APP_REDIRECT;
  }

  if (raw.startsWith("join_")) {
    const token = raw.slice(5).trim();
    if (token && /^[a-zA-Z0-9_-]+$/.test(token)) {
      return safeSameOriginPath(`/teams/join/${token}`) || DEFAULT_MINI_APP_REDIRECT;
    }
    return DEFAULT_MINI_APP_REDIRECT;
  }

  if (raw.startsWith("path_")) {
    const encoded = raw.slice(5);
    try {
      const path = decodeURIComponent(encoded);
      return safeSameOriginPath(path) || DEFAULT_MINI_APP_REDIRECT;
    } catch {
      return DEFAULT_MINI_APP_REDIRECT;
    }
  }

  if (raw.startsWith("ref_")) {
    const code = raw.slice(4).trim();
    if (code && /^[a-zA-Z0-9_-]+$/.test(code)) {
      return (
        safeSameOriginPath(`/auth/telegram/invite?ref=${encodeURIComponent(code)}`) ||
        DEFAULT_MINI_APP_REDIRECT
      );
    }
    return safeSameOriginPath("/auth/telegram/invite") || DEFAULT_MINI_APP_REDIRECT;
  }

  return DEFAULT_MINI_APP_REDIRECT;
}
